import React from 'react';
import { Form } from 'react-final-form';
import Axios from 'axios';
import { FORM_ERROR } from 'final-form';
import { ReactQueryProvider } from '@/utils/ReactQuery';
import { useMutation } from '@tanstack/react-query';
import * as Routes from 'modules/routes';
import { SubmitButton } from '@/components/ui/Buttons';
import PasswordRequirements from '@/components/shared/PasswordRequirements';
import { strictPasswordValidationError } from '@/components/shared/PasswordRequirements/helpers';
import { SubmitError } from '@/components/common/Forms/Utils';
import PasswordField from '@/components/common/Forms/User/PasswordField';

interface ChangePasswordFormProps {
  hasStrictPasswordRequirement: boolean;
  isTemporary?: boolean;
  resetPasswordToken: string;
  showCancelButton?: boolean;
}

interface FormValues {
  password: string;
  password_confirmation: string;
}

const ChangePasswordForm = ({
  hasStrictPasswordRequirement,
  isTemporary = false,
  resetPasswordToken,
  showCancelButton = true,
}: ChangePasswordFormProps) => {
  const { mutateAsync: updatePassword, isLoading } = useMutation({
    mutationFn: (values: FormValues) => Axios.put(Routes.staffer_password_path(), {
      staffer: { ...values, reset_password_token: resetPasswordToken },
    }, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    }),
    onSuccess: (response) => {
      if (response.data?.redirect_url) {
        window.location.href = response.data.redirect_url;
      } else {
        window.location.href = Routes.staffer_root_path();
      }
    },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      await updatePassword(values);
    } catch (error: any) {
      const errorMessage = error.response?.data?.errors || 'An error occurred. Please try again.';
      return { [FORM_ERROR]: errorMessage };
    }
  };

  const validate = (values: FormValues) => {
    const errors: Partial<Record<keyof FormValues, string>> = {};

    if (!values.password) {
      errors.password = 'Please enter a password.';
    } else if (hasStrictPasswordRequirement) {
      const message = strictPasswordValidationError(values.password);
      if (message) errors.password = message;
    }

    if (values.password !== values.password_confirmation) {
      errors.password_confirmation = 'Passwords do not match.';
    }

    return errors;
  };

  const title = isTemporary ? 'Set Temporary Password' : 'Set New Password';
  const confirmTitle = isTemporary ? 'Confirm Temporary Password' : 'Confirm New Password';
  const submitButtonText = isTemporary ? 'Temporarily Set My Password' : 'Change My Password';

  return (
    <Form
      initialValues={{
        password: '',
        password_confirmation: '',
      }}
      validate={validate}
      onSubmit={onSubmit}
    >
      {({ handleSubmit, pristine, submitError, values }) => (
        <form onSubmit={handleSubmit}>
          <PasswordField
            displayError={false}
            htmlFor="password"
            inputWidth={206}
            label={title}
          />
          {hasStrictPasswordRequirement && (
            <PasswordRequirements
              maxWidth={230}
              password={values.password || ''}
              spacerWidth={130}
            />
          )}
          <PasswordField
            htmlFor="password_confirmation"
            inputWidth={206}
            label={confirmTitle}
          />

          <SubmitError error={submitError} />

          <div className="mt20">
            <SubmitButton
              disabled={pristine}
              submitting={isLoading}
            >
              {submitButtonText}
            </SubmitButton>

            {showCancelButton && (
              <a
                className="btn btn--link-purple"
                href={Routes.new_staffer_session_path()}
              >
                Cancel
              </a>
            )}
          </div>
        </form>
      )}
    </Form>
  );
};

const ChangePasswordFormWrapper = (props: ChangePasswordFormProps) => (
  <ReactQueryProvider>
    <ChangePasswordForm {...props} />
  </ReactQueryProvider>
);

export default ChangePasswordFormWrapper;

